import React, { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Lock, Mail, KeyRound, Eye, EyeOff, ShieldCheck, Loader2, Building2, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AppHeader from '@/components/shared/AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = ({ user, onLogout }) => {
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordTab, setPasswordTab] = useState('direct'); // direct or otp
  
  // Direct password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [directNewPassword, setDirectNewPassword] = useState('');
  const [directConfirmPassword, setDirectConfirmPassword] = useState('');
  const [directLoading, setDirectLoading] = useState(false);
  const [showDirectPassword, setShowDirectPassword] = useState(false);
  
  // OTP flow state
  const [step, setStep] = useState('request'); // request, verify, change
  const [email, setEmail] = useState(user?.email || '');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [devOtp, setDevOtp] = useState(''); // For dev mode when email fails

  // Direct password change handler
  const handleDirectPasswordChange = async () => {
    if (directNewPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (directNewPassword !== directConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setDirectLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/change-password-direct`, {
        current_password: currentPassword,
        new_password: directNewPassword
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Password changed successfully! Confirmation email sent.');
      setChangePasswordOpen(false);
      resetState();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setDirectLoading(false);
    }
  };

  const handleRequestOtp = async () => {
    if (!email) {
      toast.error('Please enter your username or email');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/request-otp`, { email });
      
      // Update email to actual email returned from backend (for OTP verification)
      if (response.data.email) {
        setEmail(response.data.email);
      }
      
      if (response.data.otp) {
        // Email failed, OTP returned in response (dev mode)
        setDevOtp(response.data.otp);
        toast.info('OTP generated (email service unavailable). Check below for OTP.');
      } else {
        toast.success('OTP sent to your email!');
      }
      
      setOtpSent(true);
      setStep('verify');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/verify-otp`, { email, otp });
      toast.success('OTP verified!');
      setStep('change');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        email,
        otp,
        new_password: newPassword
      });
      
      toast.success('Password changed successfully! Please login again.');
      setChangePasswordOpen(false);
      resetState();
      
      // Log out user after password change
      setTimeout(() => {
        onLogout();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    // Reset direct change state
    setCurrentPassword('');
    setDirectNewPassword('');
    setDirectConfirmPassword('');
    setPasswordTab('direct');
    // Reset OTP flow state
    setStep('request');
    setOtp('');
    setNewPassword('');
    setConfirmPassword('');
    setOtpSent(false);
    setDevOtp('');
  };

  const handleDialogClose = (open) => {
    if (!open) {
      resetState();
    }
    setChangePasswordOpen(open);
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} onLogout={onLogout} showBack={true} backTo="/admin" />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-serif text-foreground mb-2">Settings</h1>
        <p className="text-muted-foreground mb-8">Manage your account and preferences</p>

        {/* Account Section */}
        <div className="glass rounded-2xl p-6 mb-6">
          <h2 className="text-xl font-serif text-foreground mb-4 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Account Security
          </h2>

          <div className="space-y-4">
            {/* Current Email Display */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Email Address</p>
                  <p className="font-medium">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* Change Password Button */}
            <div className="flex items-center justify-between p-4 bg-muted/30 rounded-xl">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Password</p>
                  <p className="font-medium">••••••••</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={() => setChangePasswordOpen(true)}
                data-testid="change-password-button"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Change Password
              </Button>
            </div>
          </div>
        </div>

        {/* Hotel User Management Section */}
        <div className="glass rounded-2xl p-6 mb-6">
          <h2 className="text-xl font-serif text-foreground mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Hotel Portal & API Management
          </h2>
          
          <p className="text-muted-foreground mb-4">
            Manage hotel portal access and enable API for hotel systems integration
          </p>
          
          <Link to="/admin/hotel-users">
            <div className="flex items-center justify-between p-4 bg-primary/10 rounded-xl hover:bg-primary/20 transition-colors cursor-pointer border-2 border-primary/30" data-testid="hotel-users-link">
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-primary" />
                <div>
                  <p className="font-medium text-primary">Manage Hotel Users & API Access</p>
                  <p className="text-sm text-muted-foreground">Create portal users • Enable API • Generate API keys</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-primary" />
            </div>
          </Link>
          
          <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
            <p className="text-sm font-medium text-blue-800 mb-2">📋 When a hotel requests API access:</p>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>Go to <strong>Manage Hotel Users & API Access</strong> above</li>
              <li>Find the hotel property in the <strong>Enable API Access</strong> section</li>
              <li>Turn ON the toggle switch for that property</li>
              <li>Copy the generated API key and share with the hotel</li>
              <li>Share the API documentation (endpoint, format, etc.)</li>
            </ol>
          </div>
        </div>

        {/* Payment & WhatsApp Section */}
        <div className="glass rounded-2xl p-6 mb-6">
          <h2 className="text-xl font-serif text-foreground mb-4">Payment & Messaging</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/admin/payment-corrections">
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-xl hover:bg-amber-100 transition-colors cursor-pointer border border-amber-200" data-testid="payment-corrections-link">
                <div>
                  <p className="font-medium text-amber-800">Payment Corrections</p>
                  <p className="text-sm text-amber-600">Edit payment records with audit trail</p>
                </div>
                <ChevronRight className="w-5 h-5 text-amber-600" />
              </div>
            </Link>
            
            <Link to="/admin/whatsapp">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors cursor-pointer border border-green-200" data-testid="whatsapp-settings-link">
                <div>
                  <p className="font-medium text-green-800">WhatsApp Configuration</p>
                  <p className="text-sm text-green-600">Automated messages • Booking alerts</p>
                </div>
                <ChevronRight className="w-5 h-5 text-green-600" />
              </div>
            </Link>
            
            <Link to="/admin/email">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors cursor-pointer border border-blue-200" data-testid="email-settings-link">
                <div>
                  <p className="font-medium text-blue-800">Email Configuration</p>
                  <p className="text-sm text-blue-600">Campaigns • Notifications • Onboarding</p>
                </div>
                <ChevronRight className="w-5 h-5 text-blue-600" />
              </div>
            </Link>
          </div>
        </div>

        {/* Therapist Login Management Section */}
        <div className="glass rounded-2xl p-6 mb-6">
          <h2 className="text-xl font-serif text-foreground mb-4 flex items-center gap-2">
            <Lock className="w-5 h-5 text-primary" />
            Therapist Login Management
          </h2>
          
          <p className="text-muted-foreground mb-4">
            Manage shared therapist login credentials for each spa property. All therapists at a property share the same login.
          </p>
          
          <Link to="/admin/therapist-logins">
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors cursor-pointer border border-purple-200" data-testid="therapist-logins-link">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="font-medium text-purple-800">Manage Therapist Logins</p>
                  <p className="text-sm text-purple-600">Reset passwords • View shared credentials • Manage PINs</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-purple-600" />
            </div>
          </Link>
          
          <div className="mt-4 p-4 bg-purple-50 rounded-xl border border-purple-200">
            <p className="text-sm font-medium text-purple-800 mb-2">🔐 Therapist Login System:</p>
            <ul className="text-sm text-purple-700 space-y-1 list-disc list-inside">
              <li>Each spa property has ONE shared login for all therapists</li>
              <li>Individual therapists are identified by their unique 4-digit PIN</li>
              <li>PINs are sent to therapists via email during onboarding</li>
              <li>If a therapist forgets their PIN, you can regenerate it here</li>
            </ul>
          </div>
        </div>

        {/* User Info Section */}
        <div className="glass rounded-2xl p-6">
          <h2 className="text-xl font-serif text-foreground mb-4">Account Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-muted/30 rounded-xl">
              <p className="text-sm text-muted-foreground">Full Name</p>
              <p className="font-medium">{user?.full_name}</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-xl">
              <p className="text-sm text-muted-foreground">Role</p>
              <p className="font-medium capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordOpen} onOpenChange={handleDialogClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-primary" />
              Change Password
            </DialogTitle>
            <DialogDescription>
              Choose your preferred method to change your password
            </DialogDescription>
          </DialogHeader>

          <Tabs value={passwordTab} onValueChange={setPasswordTab} className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="direct" data-testid="direct-change-tab">Quick Change</TabsTrigger>
              <TabsTrigger value="otp" data-testid="otp-change-tab">Via OTP</TabsTrigger>
            </TabsList>
            
            {/* Direct Password Change Tab */}
            <TabsContent value="direct" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                Enter your current password and new password. A confirmation email will be sent.
              </p>
              
              <div>
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showDirectPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    data-testid="current-password-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="directNewPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="directNewPassword"
                    type={showDirectPassword ? 'text' : 'password'}
                    value={directNewPassword}
                    onChange={(e) => setDirectNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 chars)"
                    data-testid="direct-new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowDirectPassword(!showDirectPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showDirectPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label htmlFor="directConfirmPassword">Confirm New Password</Label>
                <Input
                  id="directConfirmPassword"
                  type={showDirectPassword ? 'text' : 'password'}
                  value={directConfirmPassword}
                  onChange={(e) => setDirectConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  data-testid="direct-confirm-password-input"
                />
              </div>
              
              {directNewPassword && directConfirmPassword && directNewPassword !== directConfirmPassword && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
              
              <Button 
                onClick={handleDirectPasswordChange}
                className="w-full"
                disabled={directLoading || !currentPassword || directNewPassword.length < 8 || directNewPassword !== directConfirmPassword}
                data-testid="direct-change-submit"
              >
                {directLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Changing...</>
                ) : (
                  <>Change Password</>
                )}
              </Button>
              
              <p className="text-xs text-muted-foreground text-center">
                Confirmation email will be sent to {user?.email}
              </p>
            </TabsContent>
            
            {/* OTP Flow Tab */}
            <TabsContent value="otp" className="space-y-4 mt-4">
            {/* Step 1: Request OTP */}
            {step === 'request' && (
              <>
                <div>
                  <Label htmlFor="email">Username or Email</Label>
                  <Input
                    id="email"
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin or your@email.com"
                    data-testid="otp-email-input"
                  />
                </div>
                <Button 
                  onClick={handleRequestOtp} 
                  className="w-full"
                  disabled={loading}
                  data-testid="request-otp-button"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <>Send OTP</>
                  )}
                </Button>
              </>
            )}

            {/* Step 2: Verify OTP */}
            {step === 'verify' && (
              <>
                <div>
                  <Label htmlFor="otp">Enter OTP</Label>
                  <Input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    className="text-center text-2xl tracking-widest"
                    data-testid="otp-input"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    OTP sent to {email}
                  </p>
                </div>

                {/* Dev mode: Show OTP if email failed */}
                {devOtp && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700 mb-1">Email service unavailable. Use this OTP:</p>
                    <p className="text-2xl font-bold text-amber-800 tracking-widest text-center">{devOtp}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setStep('request')}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button 
                    onClick={handleVerifyOtp}
                    className="flex-1"
                    disabled={loading || otp.length !== 6}
                    data-testid="verify-otp-button"
                  >
                    {loading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                    ) : (
                      <>Verify OTP</>
                    )}
                  </Button>
                </div>
              </>
            )}

            {/* Step 3: Change Password */}
            {step === 'change' && (
              <>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                  ✓ OTP verified successfully
                </div>

                <div>
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    data-testid="confirm-password-input"
                  />
                </div>

                {newPassword && confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-500">Passwords do not match</p>
                )}

                <Button 
                  onClick={handleChangePassword}
                  className="w-full"
                  disabled={loading || newPassword.length < 6 || newPassword !== confirmPassword}
                  data-testid="change-password-submit"
                >
                  {loading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Changing...</>
                  ) : (
                    <>Change Password</>
                  )}
                </Button>
              </>
            )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSettings;
